import { useEffect, useState } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import Table from '../components/Table';
import LookupSelect, { LookupOption } from '../components/LookupSelect';

const fetchEmployeeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/employees');
  const employees = res.data.employees || [];
  const filtered = search ? employees.filter((e: any) => e.full_name.toLowerCase().includes(search.toLowerCase())) : employees;
  return filtered.slice(0, 20).map((e: any) => ({ value: e.id, label: `${e.full_name} (${e.code})` }));
};

export default function MaternityPaternity() {
  const [tab, setTab] = useState<'MATERNITY' | 'PATERNITY'>('MATERNITY');
  const [employeeId, setEmployeeId] = useState('');
  const [children, setChildren] = useState<any[]>([]);
  const [rows, setRows] = useState<any[]>([]);

  const [maternityForm, setMaternityForm] = useState({
    childId: '', isChildSgCitizen: false, eligibleWeeks: 16, paidWeeks: 16, utilizationMethod: 'CONTINUOUS', dueDate: '', birthDate: '', fromDate: '',
  });
  const [paternityForm, setPaternityForm] = useState({ childId: '', utilizationMethod: 'CONTINUOUS', expiryBasis: 'WEEKS16', birthDate: '', fromDate: '' });

  const load = async () => {
    const res = await api.get(tab === 'MATERNITY' ? '/lms/maternity' : '/lms/paternity');
    setRows(res.data.maternityLeaves || res.data.paternityLeaves || []);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    if (employeeId) {
      api.get(`/employees/${employeeId}/children`).then((res) => setChildren(res.data.children || []));
    } else {
      setChildren([]);
    }
  }, [employeeId]);

  const submit = async () => {
    if (!employeeId) return alert('Select an employee');
    try {
      if (tab === 'MATERNITY') {
        await api.post('/lms/maternity', { employeeId, ...maternityForm });
      } else {
        await api.post('/lms/paternity', { employeeId, ...paternityForm });
      }
      load();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to save');
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Maternity / Paternity Leave</h1>
      <div className="flex gap-2 mb-4 border-b">
        {(['MATERNITY', 'PATERNITY'] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-sm font-medium ${tab === t ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}>
            {t === 'MATERNITY' ? 'Maternity' : 'Paternity'}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-6 space-y-4">
        <LookupSelect label="Employee" value={employeeId} onChange={setEmployeeId} fetchOptions={fetchEmployeeOptions} />
        <Select
          label="Child"
          value={tab === 'MATERNITY' ? maternityForm.childId : paternityForm.childId}
          onChange={(e) => (tab === 'MATERNITY' ? setMaternityForm({ ...maternityForm, childId: e.target.value }) : setPaternityForm({ ...paternityForm, childId: e.target.value }))}
          options={children.map((c: any) => ({ value: c.id, label: `${c.child_name} (${c.date_of_birth ? new Date(c.date_of_birth).toLocaleDateString() : 'DOB unset'})` }))}
        />

        {tab === 'MATERNITY' ? (
          <div className="grid grid-cols-3 gap-4">
            <label className="flex items-center gap-2 text-sm mt-6">
              <input type="checkbox" checked={maternityForm.isChildSgCitizen} onChange={(e) => setMaternityForm({ ...maternityForm, isChildSgCitizen: e.target.checked })} /> Child is SG Citizen
            </label>
            <Select label="Eligible Weeks" value={maternityForm.eligibleWeeks} onChange={(e) => setMaternityForm({ ...maternityForm, eligibleWeeks: Number(e.target.value) })}
              options={[{ value: '16', label: '16 Weeks' }, { value: '12', label: '12 Weeks' }, { value: '8', label: '8 Weeks' }]} />
            <Input label="Paid Weeks" type="number" value={maternityForm.paidWeeks} onChange={(e) => setMaternityForm({ ...maternityForm, paidWeeks: Number(e.target.value) })} />
            <Select label="Utilization" value={maternityForm.utilizationMethod} onChange={(e) => setMaternityForm({ ...maternityForm, utilizationMethod: e.target.value })}
              options={[{ value: 'CONTINUOUS', label: 'Continuous (weeks x 7 days)' }, { value: 'FLEXIBLE', label: 'Flexible (first 8wk continuous + rest working days)' }]} />
            <Input label="Due Date" type="date" value={maternityForm.dueDate} onChange={(e) => setMaternityForm({ ...maternityForm, dueDate: e.target.value })} />
            <Input label="Birth Date" type="date" value={maternityForm.birthDate} onChange={(e) => setMaternityForm({ ...maternityForm, birthDate: e.target.value })} />
            <Input label="From Date" type="date" value={maternityForm.fromDate} onChange={(e) => setMaternityForm({ ...maternityForm, fromDate: e.target.value })} />
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            <Select label="Utilization" value={paternityForm.utilizationMethod} onChange={(e) => setPaternityForm({ ...paternityForm, utilizationMethod: e.target.value })}
              options={[{ value: 'CONTINUOUS', label: 'Continuous (2 x 7 days)' }, { value: 'FLEXIBLE', label: 'Flexible (working-day split)' }]} />
            <Select label="Expiry" value={paternityForm.expiryBasis} onChange={(e) => setPaternityForm({ ...paternityForm, expiryBasis: e.target.value })}
              options={[{ value: 'WEEKS16', label: '16 Weeks' }, { value: 'MONTHS12', label: '12 Months' }]} />
            <Input label="Birth Date" type="date" value={paternityForm.birthDate} onChange={(e) => setPaternityForm({ ...paternityForm, birthDate: e.target.value })} />
            <Input label="From Date" type="date" value={paternityForm.fromDate} onChange={(e) => setPaternityForm({ ...paternityForm, fromDate: e.target.value })} />
          </div>
        )}
        <Button onClick={submit}>Activate</Button>
      </div>

      <Table
        columns={[
          { key: 'employee', header: 'Employee', render: (r: any) => `${r.first_name} ${r.last_name} (${r.employee_code})` },
          { key: 'utilization_method', header: 'Utilization' },
          { key: 'from_date', header: 'From', render: (r: any) => (r.from_date ? new Date(r.from_date).toLocaleDateString() : '—') },
          { key: 'to_date', header: 'To', render: (r: any) => (r.to_date ? new Date(r.to_date).toLocaleDateString() : '—') },
          { key: 'status', header: 'Status' },
        ]}
        rows={rows}
      />
    </div>
  );
}
